import type { MessageProviderProps } from 'naive-ui'
import type { InjectionKey, Ref } from 'vue'

import { useMessage } from 'naive-ui'

export const DEFAULT_NOTIFICATION_PLACEMENT = 'top-right' as const satisfies MessageProviderProps['placement']

export type NotificationPlacement = NonNullable<MessageProviderProps['placement']>

export const NOTIFICATION_PLACEMENT_KEY: InjectionKey<Ref<NotificationPlacement>> = Symbol('notificationPlacement')

/** DOM-id слоя, в который телепортируются сообщения Naive UI (top layer через Popover API) */
export const NOTIFICATIONS_LAYER_ID = 'notifications-layer'

/**
 * Поднимает слой нотификаций в top layer браузера поверх нативных dialog.showModal().
 * Порядок в top layer = порядок промоушена, поэтому перед каждой нотификацией
 * слой перепромоутится (hide → show), чтобы встать выше уже открытых диалогов.
 */
export const promoteNotificationsLayer = () => {
  const layer = document.getElementById(NOTIFICATIONS_LAYER_ID)
  // Popover API может отсутствовать (старые браузеры, jsdom в тестах)
  if (!layer || typeof layer.showPopover !== 'function') {
    return
  }
  try {
    if (layer.matches(':popover-open')) {
      layer.hidePopover()
    }
    layer.showPopover()
  } catch {
    // Элемент вне DOM или недоступный селектор — деградируем до текущего поведения
  }
}

export interface NotificationCallOptions {
  // Значения для интерполяции плейсхолдеров в сообщении i18n
  params?: Record<string, unknown>
  placement?: NotificationPlacement
}

type NotificationType = 'error' | 'success'

export const useNotifications = () => {
  const { t } = useI18n()
  const notify = useMessage()
  const notificationPlacement = inject(NOTIFICATION_PLACEMENT_KEY)

  if (!notificationPlacement) {
    throw new Error(
      'notificationPlacement не найден. Убедитесь, что provide(NOTIFICATION_PLACEMENT_KEY) вызван в App.vue.',
    )
  }

  const showNotification = async (type: NotificationType, message: string, options?: NotificationCallOptions) => {
    notificationPlacement.value = options?.placement ?? DEFAULT_NOTIFICATION_PLACEMENT
    await nextTick()

    promoteNotificationsLayer()

    const text = t(message, options?.params ?? {})

    if (type === 'success') {
      notify.success(text)
      return
    }

    notify.error(text)
  }

  const successNotification = (message: string, options?: NotificationCallOptions) => {
    return showNotification('success', message, options)
  }

  const errorNotification = (message: string, options?: NotificationCallOptions) => {
    return showNotification('error', message, options)
  }

  return {
    successNotification,
    errorNotification,
  }
}
